import * as FormHelpers from '../form-helpers.js';
import { DatePicker } from '../date-picker.js';
import { getNormalizedPostalCodeValue, setupFrenchCityAutocomplete } from '../french-city-autocomplete.js';
import {
  hasTrackingConsent,
  disableTrackingForCurrentPage,
  applyConsentChoice,
  getStoredConsentChoice,
  loadTrackingAfterConsent,
  setupWebsiteEmbedConsentBridge,
  sendGAEvent,
  sendAnonymousEvent,
  setupAnonymousFormStartTracking,
  applyMetaAdvancedMatching,
} from './shared/tracking.js';

const getRuntimeConfig = () => (typeof window !== 'undefined' && window.mutualysFormData) || {};
const getTrackingFormType = () => getRuntimeConfig().form_type || 'mutualys_form';
const getSubmissionSourceType = () => getRuntimeConfig().submission_source_type || 'form';
const getTrackingSource = () => getRuntimeConfig().tracking_source || getSubmissionSourceType();
const isConfirmationVideoEnabled = () => getRuntimeConfig().confirmation_video_enabled !== false;
const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (c) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}[c]));
const getConsentCopyHtml = () => {
  const config = getRuntimeConfig();
  const consentText = config.consent_text || "J'accepte qu'un conseiller Mutualys me contacte par téléphone et SMS afin de m'aider à trouver une complémentaire santé adaptée à mes besoins. Je peux retirer mon consentement à tout moment.";
  const privacyUrl = config.privacy_url || '/mutualys/protection-donnees-personnelles/';
  const linkLabel = config.privacy_link_label || 'En savoir plus';

  return `${escapeHtml(consentText)} <a href="${escapeHtml(privacyUrl)}" target="_blank" rel="noopener">${escapeHtml(linkLabel)}</a>.`;
};

// Consent state machine, GA / Meta Pixel loaders, and the GA transport are imported
// from ./shared/tracking.js (shared with the V2 form). The wrappers below add the
// main form's event-specific parameters.
const trackStepView = (stepName, stepNumber) => {
  sendGAEvent('step_view', {
    step_name: stepName,
    step_number: stepNumber,
    form_type: getTrackingFormType(),
    event_category: 'engagement',
    event_label: 'mutualys_form_step_' + stepNumber,
  });
  trackAnonymousStepView(stepName, stepNumber);
};

const trackAnonymousStepView = (stepName, stepNumber) => sendAnonymousEvent('step_view', {
  step_name: stepName,
  step_number: stepNumber,
  form_type: getTrackingFormType(),
  source: getTrackingSource(),
});

const trackStepCompletion = (stepName, stepNumber, timeOnStep) => {
  sendGAEvent('step_complete', {
    completed_step: stepName,
    step_number: stepNumber,
    time_on_step: timeOnStep,
    form_type: getTrackingFormType(),
    event_category: 'engagement',
    event_label: 'mutualys_form_complete_step_' + stepNumber,
  });
  trackAnonymousStepCompletion(stepName, stepNumber, timeOnStep);
};

const trackAnonymousStepCompletion = (stepName, stepNumber, timeOnStep) => sendAnonymousEvent('step_complete', {
  step_name: stepName,
  step_number: stepNumber,
  elapsed_ms: timeOnStep,
  form_type: getTrackingFormType(),
  source: getTrackingSource(),
});

const trackFormAbandonment = (stepName, stepNumber, timeOnStep) => {
  sendGAEvent('form_abandon', {
    abandoned_step: stepName,
    step_number: stepNumber,
    time_on_step: timeOnStep,
    form_type: getTrackingFormType(),
    event_category: 'engagement',
    event_label: 'mutualys_form_abandon_step_' + stepNumber,
  }, { transport: 'beacon' });
  trackAnonymousFormAbandonment(stepName, stepNumber, timeOnStep);
};

const trackAnonymousFormAbandonment = (stepName, stepNumber, timeOnStep) => sendAnonymousEvent('form_abandon', {
  step_name: stepName,
  step_number: stepNumber,
  elapsed_ms: timeOnStep,
  form_type: getTrackingFormType(),
  source: getTrackingSource(),
}, { transport: 'beacon' });

const trackVideoEvent = (action, details = {}) => {
  sendGAEvent(`video_${action}`, {
    video_action: action,
    video_title: details.title || '',
    video_src: details.src || '',
    step_name: 'confirmation',
    step_number: 10,
    form_type: getTrackingFormType(),
    event_category: 'engagement',
    event_label: `mutualys_form_video_${action}`,
  });
  trackAnonymousVideoEvent(action);
};

const trackAnonymousVideoEvent = (action) => sendAnonymousEvent(`video_${action}`, {
  step_name: 'confirmation',
  step_number: 10,
  form_type: getTrackingFormType(),
  source: getTrackingSource(),
});

const getConfirmationContainer = () => (
  document.querySelector('[data-tab="confirmation"]')
  || document.getElementById('mutualys-form-confirmation')
);

const setupFinalStepVideoTracking = () => {
  const confirmationContainer = getConfirmationContainer();

  if (!confirmationContainer) return;

  const videos = confirmationContainer.querySelectorAll('video');
  videos.forEach((video, index) => {
    if (video.dataset.trackingBound === '1') return;
    video.dataset.trackingBound = '1';

    const getVideoMeta = () => ({
      title: video.getAttribute('title') || video.dataset.title || `confirmation_video_${index + 1}`,
      src: video.currentSrc || video.src || video.dataset.src || ''
    });

    video.addEventListener('play', () => {
      if (video.dataset.trackedPlay === '1') return;
      video.dataset.trackedPlay = '1';
      trackVideoEvent('play', getVideoMeta());
    });

    video.addEventListener('ended', () => {
      if (video.dataset.trackedComplete === '1') return;
      video.dataset.trackedComplete = '1';
      trackVideoEvent('complete', getVideoMeta());
    });
  });
};

const setupConsentBanner = () => {
  if (setupWebsiteEmbedConsentBridge()) return;

  let cleanupBannerSpacing = () => {};

  const clearBannerSpacing = () => {
    cleanupBannerSpacing();
    cleanupBannerSpacing = () => {};
    document.body.classList.remove('mutualys-consent-banner-open');
    document.documentElement.style.removeProperty('--mutualys-consent-banner-offset');
  };

  const applyBannerSpacing = (banner) => {
    clearBannerSpacing();
    document.body.classList.add('mutualys-consent-banner-open');

    const updateBannerOffset = () => {
      if (!document.body.contains(banner)) return;

      const bannerHeight = Math.ceil(banner.getBoundingClientRect().height);
      document.documentElement.style.setProperty(
        '--mutualys-consent-banner-offset',
        `${bannerHeight + 48}px`
      );
    };

    updateBannerOffset();

    const resizeObserver = 'ResizeObserver' in window
      ? new ResizeObserver(updateBannerOffset)
      : null;

    if (resizeObserver) {
      resizeObserver.observe(banner);
    }

    window.addEventListener('resize', updateBannerOffset);

    cleanupBannerSpacing = () => {
      window.removeEventListener('resize', updateBannerOffset);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  };

  const ensureSettingsButton = () => {
    let settingsButton = document.querySelector('[data-consent-settings]');
    if (settingsButton) return settingsButton;

    settingsButton = document.createElement('button');
    settingsButton.type = 'button';
    settingsButton.className = 'mutualys-consent-settings-button';
    settingsButton.dataset.consentSettings = '1';
    settingsButton.textContent = 'Gérer les cookies';
    settingsButton.setAttribute('aria-label', 'Gérer les préférences de cookies');
    settingsButton.addEventListener('click', () => showConsentBanner());
    document.body.appendChild(settingsButton);

    return settingsButton;
  };

  const showConsentBanner = () => {
    if (document.querySelector('.mutualys-consent-banner')) return;

    const settingsButton = ensureSettingsButton();
    settingsButton.hidden = true;

    const banner = document.createElement('div');
    banner.className = 'mutualys-consent-banner';
    banner.setAttribute('role', 'dialog');
    banner.setAttribute('aria-live', 'polite');
    banner.setAttribute('aria-label', 'Préférences de cookies');
    banner.innerHTML = `
      <div class="mutualys-consent-banner__copy">
        <strong>Respect de votre vie privée</strong>
        <span>Nous utilisons des cookies pour mesurer l'audience et améliorer votre expérience sur ce formulaire.</span>
      </div>
      <div class="mutualys-consent-banner__actions">
        <button type="button" class="btn btn-outline-primary" data-consent-choice="refused">Refuser</button>
        <button type="button" class="btn btn-primary" data-consent-choice="accepted">Accepter</button>
      </div>
    `;

    banner.addEventListener('click', (event) => {
      const choiceButton = event.target.closest('[data-consent-choice]');
      if (!choiceButton) return;

      const consentChoice = choiceButton.dataset.consentChoice;
      applyConsentChoice(consentChoice);

      clearBannerSpacing();
      banner.remove();
      settingsButton.hidden = false;
    });

    document.body.appendChild(banner);
    applyBannerSpacing(banner);
  };

  ensureSettingsButton();

  const consentChoice = getStoredConsentChoice();
  if (consentChoice === 'accepted') {
    window.mutualysTrackingDisabled = false;
    loadTrackingAfterConsent();
  } else if (consentChoice !== 'refused') {
    showConsentBanner();
  } else {
    disableTrackingForCurrentPage();
  }
};

const ensureConfirmationVideo = (container, { autoplayOnShow = true } = {}) => {
  if (!isConfirmationVideoEnabled()) return;
  if (!container) return;

  const videoWrapper = container.querySelector('[data-confirmation-video]')
    || container.querySelector('.mutualys-form-video');

  if (!videoWrapper) return;

  const existingVideo = videoWrapper.querySelector('video');
  if (existingVideo) {
    if (autoplayOnShow) {
      const playPromise = existingVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
    return;
  }

  const video = document.createElement('video');
  video.setAttribute('controls', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('preload', 'none');
  video.setAttribute('width', '100%');

  const source = document.createElement('source');
  source.src = videoWrapper.dataset.videoSrc || '/video.mp4';
  source.type = videoWrapper.dataset.videoType || 'video/mp4';

  video.appendChild(source);
  videoWrapper.appendChild(video);

  setupFinalStepVideoTracking();

  if (autoplayOnShow) {
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }
};

const confirmationVideoMarkup = (classes = 'mt-4') => (
  isConfirmationVideoEnabled()
    ? `<div class="mutualys-form-video ${classes}" data-confirmation-video data-video-src="/video.mp4" data-video-type="video/mp4"></div>`
    : ''
);

const maybeLoadConfirmationVideo = (tabOrName) => {
  if (!isConfirmationVideoEnabled()) return;
  const isConfirmation = typeof tabOrName === 'string'
    ? tabOrName === 'confirmation'
    : tabOrName?.dataset?.tab === 'confirmation';

  if (!isConfirmation) return;

  ensureConfirmationVideo(getConfirmationContainer(), { autoplayOnShow: true });
};

document.addEventListener("DOMContentLoaded", () => {
  const formContainer = document.querySelector('.mutualys-form-container');
  const form = document.querySelector('.mutualys-form');
  const formContent = form.querySelector('.mutualys-form-content');

  if (!form) return;

  setupAnonymousFormStartTracking(form, {
    form_type: getTrackingFormType(),
    source: getTrackingSource(),
  });
  setupConsentBanner();

  const formAvatarImage = formContainer.dataset.formAvatarImage;

  if (formContent.querySelector('input:not([type="hidden"])')) {
    console.log('Form already initialized');
    return;
  }



  const SVGprev = `<svg class="m-auto" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="arrow-left"><path fill="currentColor" d="M17,11H9.41l3.3-3.29a1,1,0,1,0-1.42-1.42l-5,5a1,1,0,0,0-.21.33,1,1,0,0,0,0,.76,1,1,0,0,0,.21.33l5,5a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L9.41,13H17a1,1,0,0,0,0-2Z"></path></svg>`;
  const SVGnext = `<svg class="m-auto" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="arrow-right"><path fill="currentColor" d="M17.92,11.62a1,1,0,0,0-.21-.33l-5-5a1,1,0,0,0-1.42,1.42L14.59,11H7a1,1,0,0,0,0,2h7.59l-3.3,3.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0l5-5a1,1,0,0,0,.21-.33A1,1,0,0,0,17.92,11.62Z"></path></svg>`;

  const prevButton = `<button type="button" class="btn rounded" data-tab-btn-prev>${SVGprev} Étape précédente</button>`;
  const nextButton = `<button type="button" class="btn rounded btn-primary" data-tab-btn-next>Étape suivante ${SVGnext}</button>`;

  let assistantName = getRuntimeConfig().assistant_name || '';

  if (!assistantName) {
    switch (document.location.hostname) {
      case 'normandiesante.fr':
        assistantName = 'Guillaume';
        break;
      case 'ile-de-france-sante.fr':
        assistantName = 'Louis';
        break;
      case 'hauts-de-france-sante.fr':
        assistantName = 'Gabriel';
        break;
      default:
        assistantName = 'Louis';
    }
  }

  const headerTemplate = `
    <div id="mutualys-form-header" class="row row-cols-2">
      <div class="col-auto">
        <div class="mutualys-form-avatar">
          <img src="${formAvatarImage || '/wp-content/plugins/mutualys-form/mutualys-form-avatar.png'}" alt="${assistantName}" class="rounded-circle" width="75" height="75">
          <span class="mutualys-form-avatar-badge"></span>
        </div>
      </div>
      <div class="col d-flex align-items-center flex-grow-1">
        <p class="mutualys-form-avatar-chat border border-light mb-0 px-3 py-2 rounded text-body">
          <b class="d-block">Bonjour, je suis ${assistantName}, votre conseiller en ligne.</b>
          Afin de vous aiguiller, j'ai besoin de différentes informations.
          Notre engagement : zéro revente de vos données personnelles à des sociétés tierces
        </p>
      </div>
    </div>
  `;

  const ageRangeStep = () => {
    return `
      <div class="row">
        <div class="col-12">
          <h5>Sélectionnez votre tranche d'âge :</h5>
        </div>
        <div class="col-12">
          <div class="age-range-buttons d-grid gap-3 mt-4">
            <button type="button" class="btn btn-outline-primary btn-lg age-range-btn" data-age="deadend">Moins de 50 ans</button>
            <button type="button" class="btn btn-outline-primary btn-lg age-range-btn" data-age="50-70">Entre 50 et 70 ans</button>
            <button type="button" class="btn btn-outline-primary btn-lg age-range-btn" data-age="70-80">Entre 70 et 80 ans</button>
            <button type="button" class="btn btn-outline-primary btn-lg age-range-btn" data-age="80+">Plus de 80 ans</button>
          </div>
        </div>
        <div class="col-12">
          <div class="mutualys-form-button-container">
            <button type="button" class="btn rounded btn-primary" data-tab-btn-next disabled>Étape suivante ${SVGnext}</button>
          </div>
        </div>
      </div>
    `;
  };

  const currentCoverageStep = () => {
    return `
      <div class="row">
        <div class="col-12">
          <h5>Êtes-vous couvert actuellement par une mutuelle ?</h5>
        </div>
        <div class="col-12">
          <div class="coverage-buttons d-grid gap-3 mt-4">
            <button type="button" class="btn btn-outline-primary btn-lg coverage-btn" data-coverage="yes">Oui</button>
            <button type="button" class="btn btn-outline-primary btn-lg coverage-btn" data-coverage="no">Non</button>
          </div>
        </div>
        <div class="col-12">
          <div class="mutualys-form-button-container">
            ${prevButton}
            <button type="button" class="btn rounded btn-primary" data-tab-btn-next disabled>Étape suivante ${SVGnext}</button>
          </div>
        </div>
      </div>
    `;
  };

  const currentInsurerStep = () => {
    return `
        <div class="row">
        <div class="col-12">
          <h5>Quel est votre assureur ?</h5>
        </div>
        <div class="col-12">
          <div class="insurer-buttons d-grid gap-2 mt-4">
            <button type="button" class="btn btn-outline-primary insurer-btn" data-insurer="VIASANTÉ mutuelle">VIASANTÉ mutuelle</button>
            <button type="button" class="btn btn-outline-primary insurer-btn" data-insurer="Apivia Mutuelle">Apivia Mutuelle</button>
            <button type="button" class="btn btn-outline-primary insurer-btn" data-insurer="Cegema">Cegema</button>
            <button type="button" class="btn btn-outline-primary insurer-btn" data-insurer="Harmonie Mutuelle">Harmonie Mutuelle</button>
            <button type="button" class="btn btn-outline-primary insurer-btn" data-insurer="Matmut">Matmut</button>
            <button type="button" class="btn btn-outline-primary insurer-btn" data-insurer="Malakoff Humanis">Malakoff Humanis</button>
            <button type="button" class="btn btn-outline-primary insurer-btn" data-insurer="Néoliane Santé">Néoliane Santé</button>
            <button type="button" class="btn btn-outline-primary insurer-btn" data-insurer="Autre">Autre</button>
          </div>
        </div>
        <div class="col-12">
          <div class="mutualys-form-button-container">
            ${prevButton}
            <button type="button" class="btn rounded btn-primary" data-tab-btn-next disabled>Étape suivante ${SVGnext}</button>
          </div>
        </div>
      </div>
    `;
  };

  const currentPremiumStep = () => {
    return `
        <div class="row">
        <div class="col-12">
          <h5>Combien payez-vous actuellement votre mutuelle par mois ?</h5>
        </div>
          <div class="col-12">
          <div class="premium-buttons d-grid gap-2 mt-4">
            <button type="button" class="btn btn-outline-primary premium-btn" data-premium="Moins de 70 €">Moins de 70 €</button>
            <button type="button" class="btn btn-outline-primary premium-btn" data-premium="70 - 80 €">70 - 80 €</button>
            <button type="button" class="btn btn-outline-primary premium-btn" data-premium="80 - 90 €">80 - 90 €</button>
            <button type="button" class="btn btn-outline-primary premium-btn" data-premium="90 - 130 €">90 - 130 €</button>
            <button type="button" class="btn btn-outline-primary premium-btn" data-premium="130 - 150 €">130 - 150 €</button>
            <button type="button" class="btn btn-outline-primary premium-btn" data-premium="Plus de 150 €">Plus de 150 €</button>
            <button type="button" class="btn btn-outline-primary premium-btn" data-premium="Je ne sais pas">Je ne sais pas</button>
          </div>
          </div>
          <div class="col-12">
            <div class="mutualys-form-button-container">
              ${prevButton}
            <button type="button" class="btn rounded btn-primary" data-tab-btn-next disabled>Étape suivante ${SVGnext}</button>
          </div>
        </div>
      </div>
    `;
  };

  const whoToInsureStep = () => {
    return `
      <div class="row">
        <div class="col-12">
          <h5>Qui souhaitez-vous assurer ?</h5>
        </div>
        <div class="col-12">
          <div class="who-insure-buttons d-grid gap-3 mt-4">
            <button type="button" class="btn btn-outline-primary btn-lg who-insure-btn" data-who="me">Moi</button>
            <button type="button" class="btn btn-outline-primary btn-lg who-insure-btn" data-who="couple">Mon conjoint et moi</button>
            <button type="button" class="btn btn-outline-primary btn-lg who-insure-btn" data-who="children">Mes enfants et moi</button>
            <button type="button" class="btn btn-outline-primary btn-lg who-insure-btn" data-who="family">Toute ma famille</button>
          </div>
        </div>
        <div class="col-12">
          <div class="mutualys-form-button-container">
            ${prevButton}
            <button type="button" class="btn rounded btn-primary" data-tab-btn-next disabled>Étape suivante ${SVGnext}</button>
          </div>
        </div>
      </div>
    `;
  };

  const needsStep = () => {
    return `
      <div class="row">
        <div class="col-12">
          <h5>Quels sont vos besoins ?</h5>
          <p class="text-muted">Plusieurs choix possibles</p>
        </div>
        <div class="col-12">
          <div class="needs-buttons d-grid gap-2 mt-4">
            <button type="button" class="btn btn-outline-primary needs-btn" data-need="audition">Audition</button>
            <button type="button" class="btn btn-outline-primary needs-btn" data-need="dentaire">Dentaire</button>
            <button type="button" class="btn btn-outline-primary needs-btn" data-need="optique">Optique</button>
            <button type="button" class="btn btn-outline-primary needs-btn" data-need="hospitalisation">Hospitalisation</button>
            <button type="button" class="btn btn-outline-primary needs-btn" data-need="kinesitherapie">Kinésithérapie</button>
            <button type="button" class="btn btn-outline-primary needs-btn" data-need="cures">Cures thermales</button>
            <button type="button" class="btn btn-outline-primary needs-btn" data-need="autres">Autres</button>
            <button type="button" class="btn btn-outline-primary needs-btn" data-need="aucun">Aucun besoin</button>
          </div>
            </div>
        <div class="col-12">
          <div class="mutualys-form-button-container">
            ${prevButton}
            <button type="button" class="btn btn-primary" data-tab-btn-next disabled>Étape suivante ${SVGnext}</button>
          </div>
        </div>
      </div>
    `;
  };

  const professionStep = () => {
    return `
      <div class="row">
        <div class="col-12">
          <h5>Actuellement quelle est votre profession ?</h5>
        </div>
        <div class="col-12">
          <div class="profession-buttons d-grid gap-3 mt-4">
            <button type="button" class="btn btn-outline-primary btn-lg profession-btn" data-profession="Retraité">Retraité</button>
            <button type="button" class="btn btn-outline-primary btn-lg profession-btn" data-profession="Indépendant">Indépendant</button>
            <button type="button" class="btn btn-outline-primary btn-lg profession-btn" data-profession="Salarié">Salarié</button>
            <button type="button" class="btn btn-outline-primary btn-lg profession-btn" data-profession="Sans emploi">Sans emploi</button>
          </div>
        </div>
        <div class="col-12">
          <div class="mutualys-form-button-container">
            ${prevButton}
            <button type="button" class="btn rounded btn-primary" data-tab-btn-next disabled>Étape suivante ${SVGnext}</button>
          </div>
        </div>
      </div>
    `;
  };

  const nameStep = () => {
    return `
        <div class="row">
          <div class="col-12">
            <h5>Comment vous appelez-vous ?</h5>
          </div>
          <div class="col-12">
            <div class="name-inputs d-grid mt-4">
              <div class="mb-3">${FormHelpers.text('insureds[0][firstname]', 'Prénom', true)}</div>
              <div class="mb-3">${FormHelpers.text('insureds[0][lastname]', 'Nom', true)}</div>
            </div>
          </div>
          <div class="col-12">
            <div class="col-12">
              <div class="mutualys-form-button-container">
                ${prevButton}
                ${nextButton}
              </div>
            </div>
          </div>
        </div>
    `;
  };

  const contactStep = () => {
    return `
      <div class="row">
        <div class="col-12">
          <h5>Enfin, vos coordonnées permettent de vous adresser votre offre personnalisée :</h5>
        </div>
        <div class="col-12">
          <div class="contact-inputs d-grid mt-4">
            <div>
              <div class="mb-3">
                <input class="form-control" type="text" placeholder="Tapez votre code postal ou votre ville" required autocomplete="off" data-location-search/>
                <input type="hidden" name="contact[postal_code]" value=""/>
                <input type="hidden" name="contact[locality]" value=""/>
              </div>
            </div>
            <div>
              <div class="mb-3">${FormHelpers.email('contact[email]', 'Email', true)}</div>
            </div>
            <div>
              <div class="mb-3">${FormHelpers.tel('contact[phones][0]', 'Téléphone', true)}</div>
            </div>
          </div>
        </div>
        <div class="mutualys-form-consent col-12 mt-4">
          <input type="checkbox" id="consent" name="consent" value="1" required>
          <div class="mutualys-form-consent-copy">
            <label for="consent" class="small">
              ${getConsentCopyHtml()}
            </label>
            <p class="mutualys-form-consent-note small mb-0">
              Vos données restent strictement confidentielles : elles sont utilisées uniquement pour traiter votre demande et ne sont jamais cédées à des tiers à des fins de prospection.
              Elles sont conservées pendant 36 mois maximum.
              Vous pouvez exercer vos droits à tout moment : <a href="mailto:rgpd@mutualys.fr">rgpd@mutualys.fr</a>
            </p>
          </div>
          <input type="hidden" name="assigned_user" value=""/>
          <input type="hidden" name="note" value=""/>
          <input type="hidden" name="tranche_age" value=""/>
          <input type="hidden" name="contact[country]" value="FR"/>
          <input type="hidden" name="contact[type]" value="private"/>
          <input type="hidden" name="contact[civility]" value=""/>
          <input type="hidden" name="contact[firstname]" value=""/>
          <input type="hidden" name="contact[lastname]" value=""/>
          <input type="hidden" name="insureds[0][affiliate]" value="holder"/>
          <input type="hidden" name="insureds[0][birthdate]" value=""/>
          <input type="hidden" name="insureds[0][regime]" value=""/>
          <input type="hidden" name="wishes[soins]" value="0"/>
          <input type="hidden" name="wishes[hospitalisation]" value="0"/>
          <input type="hidden" name="wishes[optique]" value="0"/>
          <input type="hidden" name="wishes[dentaire]" value="0"/>
          <input type="hidden" name="wish_auditive" value="0"/>
          <input type="hidden" name="effective_date" value=""/>
          <input type="hidden" name="need" value="new"/>
        </div>
        <div class="col-12">
          <div class="mutualys-form-button-container">
            ${prevButton}
            <button class="btn rounded btn-primary" id="mutualys-form-submit" type="button">Je reçois mon étude</button>
          </div>
          <p class="mutualys-form-submit-note small mb-0">
            <span>Étude gratuite</span>
            <span>Sans engagement</span>
            <span>Réponse adaptée à votre situation</span>
          </p>
        </div>
      </div>
    `;
  };

  const deadendStep = () => {
    return `
      <div class="row text-center">
        <div class="col-12">
          <h5>Nous n'avons pas d'offre pour le moment.</h5>
          <p class="mt-4">Merci de votre intérêt. Nous travaillons à étendre nos offres et vous contacterons dès qu'une solution adaptée sera disponible.</p>
        </div>
      </div>
    `;
  };

  const template = `
    <div class="mutualys-form-body">
      <div class="row px-4">
        <div class="mutualys-form-progress col-12 mt-4">
          <h4 class="mb-0">Optimisez vos avantages</h4>
          <p class="mb-1">Évaluation gratuite</p>
          <div class="progress">
            <div class="mutualys-form-progress-bar progress-bar" role="progressbar" style="width: 10%;" aria-valuenow="10" aria-valuemin="0" aria-valuemax="100">10%</div>
          </div>
        </div>
        <div class="col-12 active mutualys-form-tab" data-tab="age-range" data-step="1">
          ${ageRangeStep()}
        </div>
        <div class="col-12 mutualys-form-tab" data-tab="current-coverage" data-step="2">
          ${currentCoverageStep()}
        </div>
        <div class="col-12 mutualys-form-tab" data-tab="current-insurer" data-step="2.5">
          ${currentInsurerStep()}
        </div>
        <div class="col-12 mutualys-form-tab" data-tab="current-premium" data-step="4">
          ${currentPremiumStep()}
        </div>
        <div class="col-12 mutualys-form-tab" data-tab="who-to-insure" data-step="5">
          ${whoToInsureStep()}
        </div>
        <div class="col-12 mutualys-form-tab" data-tab="needs" data-step="6">
          ${needsStep()}
        </div>
        <div class="col-12 mutualys-form-tab" data-tab="profession" data-step="7">
          ${professionStep()}
        </div>
        <div class="col-12 mutualys-form-tab" data-tab="name" data-step="8">
          ${nameStep()}
        </div>
        <div class="col-12 mutualys-form-tab" data-tab="contact" data-step="9">
          ${contactStep()}
        </div>
        <div class="col-12 mutualys-form-tab" data-tab="confirmation" data-step="10">
          <div class="text-center">
            <b class="d-block">Je transmets votre dossier à un expert santé qui vous contactera dans les plus brefs délais.</b>
            ${confirmationVideoMarkup('mt-4')}
            <div class="mt-4"> 
              <a href="https://ringover.me/romainb.mutualys.xmkuiw?region=eu" target="_blank" class="btn btn-primary">NOUS APPELER</a> 
              <a href="https://calendly.com/mutualys-conseil-et-entraide/45min" target="_blank" class="btn btn-primary">PRENDRE RENDEZ-VOUS</a> 
            </div>
          </div>
        </div>
        <div class="col-12 mutualys-form-tab" data-tab="deadend" data-step="99">
          ${deadendStep()}
        </div>
      </div>
    </div>
  `;

  formContent.insertAdjacentHTML("afterbegin", template);
  formContent.insertAdjacentHTML("afterbegin", headerTemplate);
  setupFinalStepVideoTracking();
  setupFrenchCityAutocomplete({
    displayInput: document.querySelector('[data-location-search]'),
    postalInput: document.querySelector('input[name="contact[postal_code]"]'),
    cityInput: document.querySelector('input[name="contact[locality]"]'),
  });
  setupContactEmailFilter();
  setupContactPhoneFilter();

  function setupContactEmailFilter() {
    const emailInput = document.querySelector('input[name="contact[email]"]');
    if (!emailInput) return;

    const emailDomains = [
      'gmail.com',
      'outlook.com',
      'outlook.fr',
      'hotmail.com',
      'hotmail.fr',
      'live.com',
      'live.fr',
      'icloud.com',
      'yahoo.com',
      'orange.fr',
      'wanadoo.fr',
      'free.fr',
      'sfr.fr',
      'bbox.fr',
      'neuf.fr',
      'numericable.fr',
      'laposte.net'
    ];
    const unsupportedEmailChar = /[^A-Za-z0-9.!#$%&'*+/=?^_`{|}~@-]/;
    const unsupportedEmailChars = /[^A-Za-z0-9.!#$%&'*+/=?^_`{|}~@-]/g;
    const suggestionWrapper = emailInput.closest('.mb-3') || emailInput.parentElement;
    const suggestions = document.createElement('div');
    suggestions.className = 'mutualys-form-suggestions';
    suggestions.setAttribute('role', 'listbox');
    suggestions.hidden = true;
    if (suggestionWrapper) {
      suggestionWrapper.classList.add('mutualys-form-suggestions-wrap');
      suggestionWrapper.appendChild(suggestions);
    }
    let activeSuggestionIndex = -1;

    const sanitizeEmailValue = () => {
      const sanitized = emailInput.value.replace(unsupportedEmailChars, '');
      if (emailInput.value !== sanitized) {
        emailInput.value = sanitized;
      }
    };
    const hideEmailSuggestions = () => {
      suggestions.hidden = true;
      suggestions.innerHTML = '';
      activeSuggestionIndex = -1;
    };
    const getEmailSuggestions = () => {
      const [localPart, ...domainParts] = emailInput.value.split('@');
      if (!localPart || domainParts.length !== 1) return [];

      const domainStart = domainParts[0].toLowerCase();
      return emailDomains
        .filter((domain) => domain.startsWith(domainStart) && domain !== domainStart)
        .slice(0, 5)
        .map((domain) => `${localPart}@${domain}`);
    };
    const updateActiveEmailSuggestion = () => {
      suggestions.querySelectorAll('[data-email-suggestion]').forEach((option, index) => {
        option.classList.toggle('is-active', index === activeSuggestionIndex);
      });
    };
    const chooseEmailSuggestion = (value) => {
      emailInput.value = value;
      hideEmailSuggestions();
      emailInput.focus();
    };
    const updateEmailSuggestions = () => {
      const options = getEmailSuggestions();
      if (!options.length) {
        hideEmailSuggestions();
        return;
      }

      suggestions.innerHTML = '';
      const heading = document.createElement('div');
      heading.className = 'mutualys-form-suggestions-title';
      heading.textContent = 'Suggestions';
      suggestions.appendChild(heading);
      options.forEach((value, index) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'mutualys-form-suggestion';
        option.setAttribute('role', 'option');
        option.dataset.emailSuggestion = String(index);
        option.textContent = value;
        suggestions.appendChild(option);
      });
      suggestions.hidden = false;
      activeSuggestionIndex = -1;
    };

    emailInput.addEventListener('beforeinput', (event) => {
      if (event.data && event.data.length === 1 && unsupportedEmailChar.test(event.data)) {
        event.preventDefault();
      }
    });
    emailInput.addEventListener('input', () => {
      sanitizeEmailValue();
      updateEmailSuggestions();
    });
    emailInput.addEventListener('keydown', (event) => {
      if (suggestions.hidden) return;

      const options = suggestions.querySelectorAll('[data-email-suggestion]');
      if (!options.length) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        activeSuggestionIndex = (activeSuggestionIndex + 1) % options.length;
        updateActiveEmailSuggestion();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        activeSuggestionIndex = activeSuggestionIndex <= 0 ? options.length - 1 : activeSuggestionIndex - 1;
        updateActiveEmailSuggestion();
      } else if (event.key === 'Enter' && activeSuggestionIndex >= 0) {
        event.preventDefault();
        chooseEmailSuggestion(options[activeSuggestionIndex].textContent);
      } else if (event.key === 'Escape') {
        hideEmailSuggestions();
      }
    });
    suggestions.addEventListener('mousedown', (event) => {
      const option = event.target.closest('[data-email-suggestion]');
      if (!option) return;

      event.preventDefault();
      chooseEmailSuggestion(option.textContent);
    });
    emailInput.addEventListener('blur', () => {
      setTimeout(hideEmailSuggestions, 120);
    });
    emailInput.addEventListener('paste', () => {
      requestAnimationFrame(() => {
        sanitizeEmailValue();
        updateEmailSuggestions();
      });
    });
  }

  function setupContactPhoneFilter() {
    const phoneInput = document.querySelector('input[name="contact[phones][0]"]');
    if (!phoneInput) return;

    phoneInput.setAttribute('inputmode', 'numeric');
    phoneInput.setAttribute('pattern', '^0[1-9][0-9]{8}$');

    const sanitizePhoneValue = () => {
      let digitsOnly = phoneInput.value.replace(/\D/g, '');
      if (digitsOnly.startsWith('33') && digitsOnly.length >= 11) {
        digitsOnly = `0${digitsOnly.slice(2)}`;
      } else if (digitsOnly.startsWith('0033') && digitsOnly.length >= 13) {
        digitsOnly = `0${digitsOnly.slice(4)}`;
      }
      digitsOnly = digitsOnly.slice(0, 10);
      if (phoneInput.value !== digitsOnly) {
        phoneInput.value = digitsOnly;
      }
    };

    phoneInput.addEventListener('beforeinput', (event) => {
      if (event.data && event.data.length === 1 && /\D/.test(event.data)) {
        event.preventDefault();
      }
    });
    phoneInput.addEventListener('input', sanitizePhoneValue);
    phoneInput.addEventListener('paste', () => {
      requestAnimationFrame(sanitizePhoneValue);
    });
  }

  const scrollTo = (el) => {
    if (!el) return;

    const top = Math.max(0, el.getBoundingClientRect().top + window.pageYOffset);
    const scroller = document.scrollingElement || document.documentElement;

    try {
      scroller.scrollTo({ top, left: 0, behavior: 'smooth' });
    } catch (error) {
      window.scrollTo(0, top);
    }
  };

  const updateProgress = (tab) => {
    const stepAttr = tab.dataset.step;
    let progress;

    if (stepAttr === '2.5') {
      progress = 25;
    } else if (stepAttr === 'deadend') {
      progress = 100;
    } else {
      const step = parseFloat(stepAttr);
      if (isNaN(step)) {
        progress = 10; // Default fallback
      } else {
        progress = Math.min((step / 10) * 100, 100);
      }
    }

    const progressBar = document.querySelector('.mutualys-form-progress-bar');
    progressBar.style.width = `${progress}%`;
    progressBar.textContent = `${Math.round(progress)}%`;
  };

  const showValidationMessage = (message, type = 'danger') => {
    const existingMessage = document.querySelector('.alert');
    if (existingMessage) {
      existingMessage.remove();
    }

    const messageElement = document.createElement('div');
    messageElement.className = `alert alert-${type}`;
    messageElement.textContent = message;

    const activeTab = document.querySelector('.active[data-tab]');
    activeTab.insertBefore(messageElement, activeTab.firstChild);
  };

  let lastValidationIssues = [];

  const addValidationIssue = (issues, field, kind) => {
    issues.push({ field, kind });
  };

  const trackValidationBlocked = (issues = []) => {
    const activeTab = document.querySelector('.active[data-tab]');
    const stepName = activeTab?.dataset?.tab || currentStepData?.name || '';
    const stepNumber = parseFloat(activeTab?.dataset?.step || currentStepData?.number || 0) || '';
    const details = issues.length ? issues : [{ field: stepName || 'step', kind: 'blocked' }];

    details.forEach((issue) => {
      sendAnonymousEvent('validation_blocked', {
        step_name: stepName,
        step_number: stepNumber,
        form_type: getTrackingFormType(),
        source: getTrackingSource(),
        error_type: issue.kind || 'blocked',
        reason: issue.field || 'step',
      });
    });
  };

  const validateNameStep = () => {
    const firstname = document.querySelector('input[name="insureds[0][firstname]"]');
    const lastname = document.querySelector('input[name="insureds[0][lastname]"]');
    let isValid = true;
    let emptyFields = [];
    let issues = [];

    if (!firstname?.value) {
      isValid = false;
      firstname?.classList.add('is-invalid');
      emptyFields.push('Prénom');
      addValidationIssue(issues, 'firstname', 'empty');
    } else {
      firstname?.classList.remove('is-invalid');
    }

    if (!lastname?.value) {
      isValid = false;
      lastname?.classList.add('is-invalid');
      emptyFields.push('Nom');
      addValidationIssue(issues, 'lastname', 'empty');
    } else {
      lastname?.classList.remove('is-invalid');
    }

    if (!isValid) {
      showValidationMessage(`Veuillez remplir les champs obligatoires : ${emptyFields.join(', ')}`);
    }

    lastValidationIssues = issues;
    return isValid;
  };

  const validateContactStep = () => {
    const locationSearch = document.querySelector('[data-location-search]');
    const postalCode = document.querySelector('input[name="contact[postal_code]"]');
    const city = document.querySelector('input[name="contact[locality]"]');
    const email = document.querySelector('input[name="contact[email]"]');
    const phone = document.querySelector('input[name="contact[phones][0]"]');
    const consent = document.querySelector('input[name="consent"]');
    let isValid = true;
    let emptyFields = [];
    let issues = [];

    if (!postalCode?.value || !city?.value) {
      const isManualFallback = locationSearch?.dataset.manualFallback === '1';
      const invalidLocationFields = locationSearch?.dataset.manualFallback === '1'
        ? [postalCode, city]
        : [locationSearch];
      isValid = false;
      invalidLocationFields.forEach((field) => field?.classList.add('is-invalid'));
      emptyFields.push(isManualFallback ? 'Code postal et ville' : 'Code postal et ville dans la liste');

      if (isManualFallback) {
        if (!postalCode?.value) addValidationIssue(issues, 'postal_code', 'empty');
        if (!city?.value) addValidationIssue(issues, 'locality', 'empty');
      } else {
        addValidationIssue(issues, 'location', locationSearch?.value ? 'not_selected' : 'empty');
      }
    } else {
      locationSearch?.classList.remove('is-invalid');
      postalCode?.classList.remove('is-invalid');
      city?.classList.remove('is-invalid');
    }

    if (!email?.value) {
      isValid = false;
      email?.classList.add('is-invalid');
      emptyFields.push('Email');
      addValidationIssue(issues, 'email', 'empty');
    } else if (!email.checkValidity()) {
      isValid = false;
      email?.classList.add('is-invalid');
      emptyFields.push('Email (format invalide)');
      addValidationIssue(issues, 'email', 'invalid_format');
    } else {
      email?.classList.remove('is-invalid');
    }

    if (!phone?.value) {
      isValid = false;
      phone?.classList.add('is-invalid');
      emptyFields.push('Téléphone');
      addValidationIssue(issues, 'phone', 'empty');
    } else {
      phone?.classList.remove('is-invalid');
    }

    // Validate phone number format
    if (phone?.value && !FormHelpers.validatePhoneNumber(phone.value)) {
      isValid = false;
      phone?.classList.add('is-invalid');
      emptyFields.push('Téléphone (format invalide)');
      addValidationIssue(issues, 'phone', 'invalid_format');
    }

    if (!consent?.checked) {
      isValid = false;
      consent?.classList.add('is-invalid');
      emptyFields.push('Consentement');
      addValidationIssue(issues, 'consent', 'unchecked');
    } else {
      consent?.classList.remove('is-invalid');
    }

    if (!isValid) {
      showValidationMessage(`Veuillez remplir les champs obligatoires : ${emptyFields.join(', ')}`);
    }

    lastValidationIssues = issues;
    return isValid;
  };

  const validateTab = () => {
    lastValidationIssues = [];
    const activeTab = document.querySelector('.active[data-tab]');
    const tabName = activeTab.dataset.tab;

    switch (tabName) {
      case 'name':
        return validateNameStep();
      case 'contact':
        return validateContactStep();
      default:
        return true;
    }
  };

  const submitButton = document.getElementById('mutualys-form-submit');
  submitButton.addEventListener('click', (event) => {
    if (!validateTab()) {
      trackValidationBlocked(lastValidationIssues);
      return;
    }

    // Ensure note contains all accumulated info before serialization
    updateNoteField();

    // Ensure regime is set according to chosen profession as a final guard
    const regimeField = document.querySelector('input[name="insureds[0][regime]"]');
    if (regimeField && !regimeField.value && formData.profession) {
      regimeField.value = mapProfessionToRegime(formData.profession);
    }

    const postalCodeField = document.querySelector('input[name="contact[postal_code]"]');
    if (postalCodeField) {
      postalCodeField.value = getNormalizedPostalCodeValue(postalCodeField.value);
    }
    normalizeSubmissionDates();

    const formData = new FormData(form);
    formData.append('_wpnonce', mutualysFormData.nonce);
    formData.append('submission_source_type', getSubmissionSourceType());
    formData.append('mutualys_tracking_consent', hasTrackingConsent() ? 'accepted' : 'refused');
    submitButton.disabled = true;
    sendAnonymousEvent('submit_attempt', {
      step_name: currentStepData.name,
      step_number: currentStepData.number,
      form_type: getTrackingFormType(),
      source: getTrackingSource(),
    });

    fetch(mutualysFormData.rest_url, {
      method: 'POST',
      body: formData
    })
      .then(response => response.json().catch(() => ({})).then(data => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || !data?.submission_id) {
          throw new Error(data?.message || 'Submission failed');
        }

        sendAnonymousEvent('submit_success', {
          step_name: 'confirmation',
          step_number: 10,
          form_type: getTrackingFormType(),
          source: getTrackingSource(),
        });
        // Track FBQ if UTMs are present
        if (hasTrackingConsent() && typeof fbq !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search);
          const utmSource = urlParams.get('utm_source');

          if (utmSource === 'meta') {
            // event_id must match what the CAPI server sends ('lead_' + submission_id)
            // so Meta dedups the two events for consented users. Non-consented users
            // never reach this block — CAPI fires alone for them under LDU.
            // value+currency are sourced from PHP (per-project admin map) so Meta's
            // Lead diagnostics stop flagging malformed currency data; the previous
            // `value: formData` was the FormData object itself, which serialized to
            // garbage and broke ROAS calculation.
            const eventId = 'lead_' + data.submission_id;
            const leadValue = mutualysFormData.lead_value;
            const leadCurrency = mutualysFormData.lead_currency || 'EUR';
            applyMetaAdvancedMatching(formData);
            fbq('track', 'Lead', { value: leadValue, currency: leadCurrency, variant: 'v1' }, { eventID: eventId });
            fbq('track', 'CompleteRegistration', { value: leadValue, currency: leadCurrency, variant: 'v1' }, { eventID: eventId });
          }
        }

        formContent.innerHTML = `
          <div id="mutualys-form-confirmation" class="row">
            <div class="col-12">
              <b class="d-block text-center">Je transmets votre dossier à un expert santé qui vous contactera dans les plus brefs délais.</b>
              ${getRuntimeConfig().sms_enabled && data.sms_scheduled ? `<p class="text-center mt-2">Vous allez recevoir un SMS de confirmation.</p>` : ''}
              ${confirmationVideoMarkup('mt-4 text-center')}
              <div id="mutualys-form-confirmation-buttons" class="text-center mt-4"> 
                <a href="https://ringover.me/romainb.mutualys.xmkuiw?region=eu" target="_blank" class="btn btn-primary">NOUS APPELER</a> 
                <a href="https://calendly.com/mutualys-conseil-et-entraide/45min" target="_blank" class="btn btn-primary">PRENDRE RENDEZ-VOUS</a> 
              </div>
            </div>
          </div>
        `;

        ensureConfirmationVideo(getConfirmationContainer(), { autoplayOnShow: true });
        goToNextStep('confirmation');
      })
      .catch(error => {
        sendAnonymousEvent('submit_error', {
          step_name: currentStepData.name,
          step_number: currentStepData.number,
          form_type: getTrackingFormType(),
          source: getTrackingSource(),
          error_type: 'network_or_server',
        });
        if (document.querySelector('.alert')) {
          document.querySelector('.alert').remove();
        }
        formContent.insertAdjacentHTML("afterbegin", `
        <div class="alert alert-danger">
          Une erreur est survenue. Veuillez réessayer.
        </div>
      `);
        submitButton.disabled = false;
      });
  });

  let formData = {
    ageRange: null,
    currentCoverage: null,
    currentInsurer: null,
    currentPremium: null,
    whoToInsure: null,
    needs: [],
    profession: null
  };

  const stepRoutes = {
    'age-range': 1,
    'current-coverage': 2,
    'current-insurer': 2.5,
    'current-premium': 4,
    'who-to-insure': 5,
    'needs': 6,
    'profession': 7,
    'name': 8,
    'contact': 9,
    'confirmation': 10,
    'deadend': 99
  };

  const enableNextButton = (stepTab) => {
    const nextBtn = stepTab.querySelector('[data-tab-btn-next]');
    if (nextBtn) {
      nextBtn.disabled = false;
      nextBtn.classList.remove('disabled');
    }
  };

  const disableNextButton = (stepTab) => {
    const nextBtn = stepTab.querySelector('[data-tab-btn-next]');
    if (nextBtn) {
      nextBtn.disabled = true;
      nextBtn.classList.add('disabled');
    }
  };

  const updateBrowserHistory = (tabName) => {
    const stepNumber = stepRoutes[tabName];
    if (stepNumber > 0) {
      const url = new URL(window.location);
      url.searchParams.set('step', stepNumber);
      window.history.pushState({ step: stepNumber, tab: tabName }, '', url);
    }
  };

  const navigateToStepFromHistory = (stepNumber) => {
    const tabName = Object.keys(stepRoutes).find(key => stepRoutes[key] === stepNumber);
    if (tabName) {
      const targetTab = document.querySelector(`[data-tab="${tabName}"]`);
      const activeTab = document.querySelector('.active[data-tab]');

      if (targetTab && activeTab !== targetTab) {
        activeTab.classList.remove('active');
        targetTab.classList.add('active');

        currentStepData = {
          name: targetTab.dataset.tab,
          number: parseFloat(targetTab.dataset.step) || 1
        };
        stepStartTime = Date.now();

        trackStepView(currentStepData.name, currentStepData.number);
        maybeLoadConfirmationVideo(targetTab);

        updateProgress(targetTab);
        scrollTo(form);
      }
    }
  };

  const goToNextStep = (targetTab = null) => {
    const activeTab = document.querySelector('.active[data-tab]');
    let nextTab;

    if (targetTab) {
      nextTab = document.querySelector(`[data-tab="${targetTab}"]`);
    } else {
      nextTab = activeTab.nextElementSibling;
    }

    if (nextTab && nextTab.classList.contains('mutualys-form-tab')) {
      const timeOnStep = Date.now() - stepStartTime;
      trackStepCompletion(currentStepData.name, currentStepData.number, timeOnStep);

      activeTab.classList.remove('active');
      nextTab.classList.add('active');

      currentStepData = {
        name: nextTab.dataset.tab,
        number: parseFloat(nextTab.dataset.step) || 1
      };
      stepStartTime = Date.now();

      updateBrowserHistory(nextTab.dataset.tab);

      if (hasTrackingConsent() && typeof fbq !== 'undefined') {
        fbq('track', 'View Content', { value: nextTab.dataset.tab });
      }

      trackStepView(currentStepData.name, currentStepData.number);
      maybeLoadConfirmationVideo(nextTab);

      scrollTo(form);
      updateProgress(nextTab);
    }
  };

  const goToPrevStep = () => {
    const activeTab = document.querySelector('.active[data-tab]');
    const allTabs = document.querySelectorAll('.mutualys-form-tab');
    const currentIndex = Array.from(allTabs).indexOf(activeTab);

    if (currentIndex > 0) {
      let prevTab = allTabs[currentIndex - 1];

      if (prevTab.dataset.tab === 'current-insurer' && formData.currentCoverage !== 'yes') {
        prevTab = allTabs[currentIndex - 2];
      }

      activeTab.classList.remove('active');
      prevTab.classList.add('active');

      currentStepData = {
        name: prevTab.dataset.tab,
        number: parseFloat(prevTab.dataset.step) || 1
      };
      stepStartTime = Date.now();

      if (hasTrackingConsent() && typeof fbq !== 'undefined') {
        fbq('track', 'View Content', { value: prevTab.dataset.tab });
      }

      trackStepView(currentStepData.name, currentStepData.number);

      scrollTo(form);
      updateProgress(prevTab);
    }
  };

  const formatDateDMY = (date) => {
    return [
      String(date.getDate()).padStart(2, '0'),
      String(date.getMonth() + 1).padStart(2, '0'),
      date.getFullYear()
    ].join('/');
  };

  const normalizeDateValueToYMD = (value) => {
    if (!value) return '';

    const normalizedValue = String(value).trim();
    let matches = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (matches) {
      return `${matches[1]}-${matches[2]}-${matches[3]}`;
    }

    matches = normalizedValue.match(/^(\d{2})[\/.-](\d{2})[\/.-](\d{4})$/);
    if (matches) {
      return `${matches[3]}-${matches[2]}-${matches[1]}`;
    }

    return normalizedValue;
  };

  const normalizeSubmissionDates = () => {
    form.querySelectorAll('input[name^="insureds["][name$="[birthdate]"]').forEach((field) => {
      field.value = normalizeDateValueToYMD(field.value);
    });
  };

  const setBirthDateFromAge = (ageRange) => {
    const birthdateField = document.querySelector('input[name="insureds[0][birthdate]"]');
    if (!birthdateField) return;

    const currentYear = new Date().getFullYear();
    let birthYear;

    switch (ageRange) {
      case '50-70':
        birthYear = currentYear - 65;
        break;
      case '70-80':
        birthYear = currentYear - 75;
        break;
      case '80+':
        birthYear = currentYear - 85;
        break;
      default:
        birthYear = currentYear - 65;
    }

    birthdateField.value = `${birthYear}-01-01`;
  };

  const updateNoteField = () => {
    const noteField = document.querySelector('input[name="note"]');
    const { location } = document;
    const urlParams = new URLSearchParams(location.search);
    const utmSource = urlParams.get('utm_source');

    const noteData = [
      `Tranche d'âge: ${formData.ageRange || 'Non spécifié'}`,
      `Couverture actuelle: ${formData.currentCoverage || 'Non spécifié'}`,
      `Assureur actuel: ${formData.currentInsurer || 'Non applicable'}`,
      `Prime actuelle: ${formData.currentPremium || 'Non spécifié'}`,
      `Qui assurer: ${formData.whoToInsure || 'Non spécifié'}`,
      `Besoins: ${formData.needs.length > 0 ? formData.needs.join(', ') : 'Non spécifié'}`,
      `Profession: ${formData.profession || 'Non spécifié'}`,
      `Site: ${location.hostname}`,
      `Source: ${utmSource || 'website'}`
    ];

    const existingValue = (noteField?.value || '').trim();
    const newBlock = noteData.join('\n');

    if (!noteField) return;
    noteField.value = existingValue ? `${existingValue} | ${newBlock}` : newBlock;
  };

  const updateWishesFromNeeds = () => {
    const wishesMapping = {
      'audition': 'wish_auditive',
      'dentaire': 'wishes[dentaire]',
      'optique': 'wishes[optique]',
      'hospitalisation': 'wishes[hospitalisation]'
    };

    Object.keys(wishesMapping).forEach(need => {
      const field = document.querySelector(`input[name="${wishesMapping[need]}"]`);
      if (field) {
        field.value = formData.needs.includes(need) ? '5' : '0';
      }
    });

    const soinsField = document.querySelector('input[name="wishes[soins]"]');
    if (soinsField) {
      soinsField.value = formData.needs.length > 0 ? '5' : '0';
    }
  };

  const mapProfessionToRegime = (profession) => {
    // Profession values come from buttons: Retraité, Indépendant, Salarié, Sans emploi
    switch (profession) {
      case 'Salarié':
        return 'salarie';
      case 'Indépendant':
        return 'tns';
      case 'Retraité':
        return 'retraite_salarie';
      case 'Sans emploi':
        // Default to salarie for general coverage if unemployed; adjust if needed
        return 'salarie';
      default:
        return '';
    }
  };

  document.addEventListener('click', (e) => {
    if (e.target.classList.contains('age-range-btn')) {
      const ageRange = e.target.dataset.age;
      formData.ageRange = ageRange;
      const trancheAgeField = document.querySelector('input[name="tranche_age"]');
      if (trancheAgeField) trancheAgeField.value = ageRange;

      e.target.parentElement.querySelectorAll('.age-range-btn').forEach(btn => {
        btn.classList.remove('btn-primary');
        btn.classList.add('btn-outline-primary');
      });
      e.target.classList.remove('btn-outline-primary');
      e.target.classList.add('btn-primary');

      const activeTab = document.querySelector('.active[data-tab]');
      enableNextButton(activeTab);

      setTimeout(() => {
        if (ageRange === 'deadend') {
          hasReachedTerminalStep = true;
          goToNextStep('deadend');
          sendAnonymousEvent('dead_end', {
            form_type: getTrackingFormType(),
            source: getTrackingSource(),
            step_name: 'deadend',
            step_number: 99,
            reason: 'age_under_50',
          });
        } else {
          setBirthDateFromAge(ageRange);
          goToNextStep();
        }
      }, 500);
    }

    if (e.target.classList.contains('coverage-btn')) {
      const coverage = e.target.dataset.coverage;
      formData.currentCoverage = coverage;

      e.target.parentElement.querySelectorAll('.coverage-btn').forEach(btn => {
        btn.classList.remove('btn-primary');
        btn.classList.add('btn-outline-primary');
      });
      e.target.classList.remove('btn-outline-primary');
      e.target.classList.add('btn-primary');

      const activeTab = document.querySelector('.active[data-tab]');
      enableNextButton(activeTab);

      setTimeout(() => {
        if (coverage === 'yes') {
          goToNextStep();
        } else {
          goToNextStep('current-premium');
        }
      }, 500);
    }

    if (e.target.classList.contains('insurer-btn')) {
      const insurer = e.target.dataset.insurer;
      formData.currentInsurer = insurer;

      e.target.parentElement.querySelectorAll('.insurer-btn').forEach(btn => {
        btn.classList.remove('btn-primary');
        btn.classList.add('btn-outline-primary');
      });
      e.target.classList.remove('btn-outline-primary');
      e.target.classList.add('btn-primary');

      const activeTab = document.querySelector('.active[data-tab]');
      enableNextButton(activeTab);

      setTimeout(() => {
        goToNextStep();
      }, 500);
    }

    if (e.target.classList.contains('premium-btn')) {
      const premium = e.target.dataset.premium;
      formData.currentPremium = premium;

      e.target.parentElement.querySelectorAll('.premium-btn').forEach(btn => {
        btn.classList.remove('btn-primary');
        btn.classList.add('btn-outline-primary');
      });
      e.target.classList.remove('btn-outline-primary');
      e.target.classList.add('btn-primary');

      const activeTab = document.querySelector('.active[data-tab]');
      enableNextButton(activeTab);

      setTimeout(() => {
        goToNextStep();
      }, 500);
    }

    if (e.target.classList.contains('who-insure-btn')) {
      const who = e.target.dataset.who;
      formData.whoToInsure = who;

      e.target.parentElement.querySelectorAll('.who-insure-btn').forEach(btn => {
        btn.classList.remove('btn-primary');
        btn.classList.add('btn-outline-primary');
      });
      e.target.classList.remove('btn-outline-primary');
      e.target.classList.add('btn-primary');

      const activeTab = document.querySelector('.active[data-tab]');
      enableNextButton(activeTab);

      setTimeout(() => {
        goToNextStep();
      }, 500);
    }

    if (e.target.classList.contains('needs-btn')) {
      const need = e.target.dataset.need;

      if (need === 'aucun') {
        formData.needs = ['aucun'];
        e.target.parentElement.querySelectorAll('.needs-btn').forEach(btn => {
          btn.classList.remove('btn-primary');
          btn.classList.add('btn-outline-primary');
        });
        e.target.classList.remove('btn-outline-primary');
        e.target.classList.add('btn-primary');
      } else {
        if (formData.needs.includes('aucun')) {
          formData.needs = [];
          const aucunBtn = e.target.parentElement.querySelector('[data-need="aucun"]');
          aucunBtn.classList.remove('btn-primary');
          aucunBtn.classList.add('btn-outline-primary');
        }

        if (formData.needs.includes(need)) {
          formData.needs = formData.needs.filter(n => n !== need);
          e.target.classList.remove('btn-primary');
          e.target.classList.add('btn-outline-primary');
        } else {
          formData.needs.push(need);
          e.target.classList.remove('btn-outline-primary');
          e.target.classList.add('btn-primary');
        }
      }

      const activeTab = document.querySelector('.active[data-tab]');
      if (formData.needs.length > 0) {
        enableNextButton(activeTab);
      } else {
        disableNextButton(activeTab);
      }
    }

    if (e.target.classList.contains('profession-btn')) {
      const profession = e.target.dataset.profession;
      formData.profession = profession;

      e.target.parentElement.querySelectorAll('.profession-btn').forEach(btn => {
        btn.classList.remove('btn-primary');
        btn.classList.add('btn-outline-primary');
      });
      e.target.classList.remove('btn-outline-primary');
      e.target.classList.add('btn-primary');

      // Set regime based on profession
      const regimeValue = mapProfessionToRegime(profession);
      const regimeField = document.querySelector('input[name="insureds[0][regime]"]');
      if (regimeField) regimeField.value = regimeValue;

      const activeTab = document.querySelector('.active[data-tab]');
      enableNextButton(activeTab);

      setTimeout(() => {
        goToNextStep();
      }, 500);
    }
  });

  const nextButtons = document.querySelectorAll('[data-tab-btn-next]');
  const prevButtons = document.querySelectorAll('[data-tab-btn-prev]');

  nextButtons.forEach(button => {
    button.addEventListener('click', () => {
      if (button.disabled) return;

      const activeTab = document.querySelector('.active[data-tab]');

      if (activeTab.dataset.tab === 'needs') {
        updateWishesFromNeeds();
      }

      if (activeTab.dataset.tab === 'name') {
        if (!validateTab()) return;

        const firstname = document.querySelector('input[name="insureds[0][firstname]"]');
        const lastname = document.querySelector('input[name="insureds[0][lastname]"]');

        const contactFirstname = document.querySelector('input[name="contact[firstname]"]');
        const contactLastname = document.querySelector('input[name="contact[lastname]"]');

        if (contactFirstname) contactFirstname.value = firstname.value;
        if (contactLastname) contactLastname.value = lastname.value;
      }

      if (activeTab.dataset.tab === 'contact') {
        if (!validateTab()) return;

        updateNoteField();
        const effectiveDateField = document.querySelector('input[name="effective_date"]');
        const today = new Date();
        const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        effectiveDateField.value = formatDateDMY(nextMonth);
      }

      goToNextStep();
    });
  });

  prevButtons.forEach(button => {
    button.addEventListener('click', () => {
      goToPrevStep();
    });
  });

  const datePicker = new DatePicker();
  datePicker.initAllDatePickers(document.querySelector('.mutualys-form'));

  window.addEventListener('popstate', (event) => {
    if (event.state && event.state.step) {
      navigateToStepFromHistory(event.state.step);
    }
  });

  const urlParams = new URLSearchParams(window.location.search);
  const stepParam = urlParams.get('step');
  if (stepParam) {
    const stepNumber = parseFloat(stepParam);
    navigateToStepFromHistory(stepNumber);
  } else {
    updateBrowserHistory('age-range');
  }

  // Add utm params to hidden fields
  const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'project'];

  utmParams.forEach(param => {
    let value = urlParams.get(param);
    if (value) {
      if (param === 'project') {
        value = value.toLowerCase();
      }

      const hiddenField = document.createElement('input');
      hiddenField.type = 'hidden';
      hiddenField.name = param;
      hiddenField.value = value;
      form.appendChild(hiddenField);
    }
  });

  // Add Google Analytics client ID if available
  function getGAClientId() {
    if (!hasTrackingConsent()) {
      return Promise.resolve(null);
    }

    if (typeof gtag !== 'undefined') {
      return new Promise((resolve) => {
        gtag('get', 'GA_MEASUREMENT_ID', 'client_id', (clientId) => {
          resolve(clientId);
        });
      });
    }
    
    if (typeof ga !== 'undefined' && ga.getAll) {
      const tracker = ga.getAll()[0];
      if (tracker) {
        return Promise.resolve(tracker.get('clientId'));
      }
    }

    const gaCookie = document.cookie.split(';').find(c => c.trim().startsWith('_ga='));
    if (gaCookie) {
      const parts = gaCookie.split('=')[1].split('.');
      if (parts.length >= 4) {
        return Promise.resolve(parts[2] + '.' + parts[3]);
      }
    }

    return Promise.resolve(null);
  }

  getGAClientId().then((clientId) => {
    if (clientId) {
      const clientIdField = document.createElement('input');
      clientIdField.type = 'hidden';
      clientIdField.name = 'ga_client_id';
      clientIdField.value = clientId;
      form.appendChild(clientIdField);
      
      window.mutualysGAClientId = clientId;
    }
  });

  let stepStartTime = Date.now();
  let currentStepData = {
    name: 'age-range',
    number: 1
  };
  let hasReachedTerminalStep = false;


  window.addEventListener('beforeunload', () => {
    if (hasReachedTerminalStep) return;

    const timeOnStep = Date.now() - stepStartTime;
    trackFormAbandonment(currentStepData.name, currentStepData.number, timeOnStep);
  });

  document.addEventListener('visibilitychange', () => {
    if (hasReachedTerminalStep) return;

    if (document.visibilityState === 'hidden') {
      const timeOnStep = Date.now() - stepStartTime;
      trackFormAbandonment(currentStepData.name, currentStepData.number, timeOnStep);
    }
  });

  setTimeout(() => {
    trackStepView(currentStepData.name, currentStepData.number);
  }, 1000);

  // Tracking
  const inputFields = document.querySelectorAll('input');

  // Loop through each input field
  inputFields.forEach(input => {
    // Add an event listener for the 'change' event
    input.addEventListener('change', (event) => {
      // Get the input field's name or ID
      const inputName = event.target.name || event.target.id;

      // Send an event to Facebook Pixel (FBQ)
      if (hasTrackingConsent() && typeof fbq !== 'undefined') {
        fbq('trackCustom', 'InputChanged', {
          inputName: inputName,
          inputValue: event.target.value
        });
      }
    });
  });

  setTimeout(() => {
    const lastnameField = document.querySelector('input[name="contact[lastname]"]');
    const firstnameField = document.querySelector('input[name="contact[firstname]"]');
    const insuredLastnameField = document.querySelector('input[name="insureds[0][lastname]"]');
    const insuredFirstnameField = document.querySelector('input[name="insureds[0][firstname]"]');

    if (insuredLastnameField && lastnameField) {
      insuredLastnameField.addEventListener("input", () => {
        lastnameField.value = insuredLastnameField.value;
      });
    }

    if (insuredFirstnameField && firstnameField) {
      insuredFirstnameField.addEventListener("input", () => {
        firstnameField.value = insuredFirstnameField.value;
      });
    }
  }, 100);
});

// TESTING

// if page loads with debug=1, show all steps at once, fill all fields with random data
if (window.location.search.includes('debug=1')) {
  document.addEventListener('DOMContentLoaded', () => {
    const form = document.querySelector('.mutualys-form');
    const defaultUtmValues = {
      utm_source: 'debug',
      utm_medium: 'test',
      utm_campaign: 'mutualys_form_debug',
      utm_term: 'debug',
      utm_content: 'debug',
      project: 'mutualys'
    };
    if (form) {
      Object.entries(defaultUtmValues).forEach(([name, value]) => {
        const existingField = form.querySelector(`input[name="${name}"]`);
        if (existingField) {
          if (!existingField.value) existingField.value = value;
          return;
        }
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = name;
        hiddenField.value = value;
        form.appendChild(hiddenField);
      });
    }

    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const effectiveDate = formatDateDMY(nextMonth);
    const birthdate = `${today.getFullYear() - 65}-01-01`;

    document.querySelectorAll('[data-tab]').forEach(step => {
      step.classList.add('active');
      step.querySelectorAll('input, textarea').forEach(field => {
        if (field.name === 'contact[postal_code]') {
          field.value = '77700';
          return;
        }
        if (field.name === 'contact[locality]') {
          field.value = 'Serris';
          return;
        }
        if (field.hasAttribute('data-location-search')) {
          field.value = '77700 - Serris';
          return;
        }
        if (field.type === 'hidden') return;
        if (field.type === 'email') {
          field.value = 'test@example.com';
          return;
        }
        if (field.type === 'tel') {
          field.value = '0601020304';
          return;
        }
        field.value = Math.random().toString(36).substring(2, 15);
      });
      step.querySelectorAll('select').forEach(field => {
        field.value = field.options[Math.floor(Math.random() * field.options.length)].value;
      });
      step.querySelectorAll('input[type="radio"]').forEach(field => {
        field.checked = true;
      });
      step.querySelectorAll('input[type="checkbox"]').forEach(field => {
        field.checked = true;
      });
    });

    const buttonGroups = new Map();
    document.querySelectorAll('button').forEach(button => {
      const customClass = Array.from(button.classList).find(className => className.endsWith('-btn'));
      if (!customClass) return;
      if (!buttonGroups.has(customClass)) {
        buttonGroups.set(customClass, []);
      }
      buttonGroups.get(customClass).push(button);
    });
    buttonGroups.forEach(buttons => {
      const targetButton = buttons.length > 1 ? buttons[1] : buttons[0];
      if (targetButton) targetButton.click();
    });

    const birthdateField = document.querySelector('input[name="insureds[0][birthdate]"]');
    if (birthdateField) birthdateField.value = birthdate;
    const effectiveDateField = document.querySelector('input[name="effective_date"]');
    if (effectiveDateField) effectiveDateField.value = effectiveDate;
  });
}
